import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Chip,
  Grid,
  LinearProgress,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import LocationOn from "@mui/icons-material/LocationOn";
import PersonOutline from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import SarIcon from "../../icons/SarIcon";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Groups from "@mui/icons-material/Groups";
import ShowChart from "@mui/icons-material/ShowChart";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InsertDriveFile from "@mui/icons-material/InsertDriveFile";
import OpenInNew from "@mui/icons-material/OpenInNew";
import CheckCircle from "@mui/icons-material/CheckCircle";
import HighlightOff from "@mui/icons-material/HighlightOff";
import TagOutlined from "@mui/icons-material/TagOutlined";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetAdminAssetDetailQuery,
  useApproveAdminAssetMutation,
  useRejectAdminAssetMutation,
  useGetVerificationDetailQuery,
} from "../../../redux/api/authApi";
import { VerificationReviewDialog } from "../AdminVerifications/AdminVerifications";
import { RejectAssetModal } from "./RejectAssetModal";

const STATUS = {
  UNDER_REVIEW: "Under Review",
  LIVE: "Live",
  REJECTED: "Rejected",
  FUNDED: "Funded",
  APPROVED: "Approved",
};

const STATUS_CHIP_KEY = {
  [STATUS.UNDER_REVIEW]: "pending",
  [STATUS.LIVE]: "success",
  [STATUS.REJECTED]: "error",
  [STATUS.FUNDED]: "success",
  [STATUS.APPROVED]: "success",
};

function normalizeUiStatus(status) {
  const s = String(status || "").toLowerCase().trim();
  if (s.includes("reject")) return STATUS.REJECTED;
  if (s.includes("funded")) return STATUS.FUNDED;
  if (s.includes("approved")) return STATUS.APPROVED;
  if (
    s.includes("live") ||
    s.includes("deploy") ||
    s === "active"
  ) {
    return STATUS.LIVE;
  }
  return STATUS.UNDER_REVIEW;
}

function formatSar(v) {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "SAR 0";
  const hasDecimals = Math.abs(n % 1) > 1e-9;
  return (
    "SAR " +
    new Intl.NumberFormat("en-SA", {
      maximumFractionDigits: hasDecimals ? 2 : 0,
      minimumFractionDigits: 0,
    }).format(n)
  );
}

function formatListedDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `Listed ${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}

function adminDocTypeLabel(type) {
  const t = String(type || "").toLowerCase();
  if (t === "valuationreport" || t === "valuation_report") return "Valuation Report";
  if (t === "legaldocument" || t === "legal_document") return "Legal";
  if (t === "spvdocument" || t === "spv_document") return "SPV";
  if (t === "titledeed" || t === "title_deed") return "Title Deed";
  return type ? String(type).replace(/([A-Z])/g, " $1").trim() : "Document";
}

function normalizeLocationString(v) {
  const s = v == null ? "" : String(v).trim();
  if (!s || s === "-" || s === "—") return "";
  return s;
}

function docDisplayTitle(type) {
  const t = String(type || "").toLowerCase();
  if (t === "valuationreport" || t === "valuation_report") return "Valuation Report";
  if (t === "legaldocument" || t === "legal_document") return "Legal Document";
  if (t === "spvdocument" || t === "spv_document") return "SPV Document";
  if (t === "titledeed" || t === "title_deed") return "Title Deed";
  return adminDocTypeLabel(type);
}

function categoryTagColor(type) {
  const t = String(type || "").toLowerCase();
  if (t.includes("valuation") || t.includes("financial")) return { bg: "#fce7f3", color: "#be185d" };
  if (t.includes("legal") || t.includes("title")) return { bg: "#ede9fe", color: "#6d28d9" };
  if (t.includes("compliance")) return { bg: "#f3e8ff", color: "#7c3aed" };
  return { bg: "#f1f5f9", color: "#475569" };
}

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`asset-tabpanel-${index}`}>
      {value === index ? <Box sx={{ pt: 2.5 }}>{children}</Box> : null}
    </div>
  );
}

const StatCard = ({ icon, title, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 2,
      border: (theme) => `1px solid ${theme.palette.border.light}`,
      boxShadow: (theme) => theme.palette.shadow.statCard,
      height: "100%",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          bgcolor: "background.subtle",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: "text.muted", mb: 0.5, fontSize: 13 }}>
          {title}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", fontSize: 18 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

function StatusBadge({ status }) {
  const key = STATUS_CHIP_KEY[status] || "pending";
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        height: 26,
        px: 0.5,
        bgcolor: (theme) => theme.palette.chips[key].bg,
        color: (theme) => theme.palette.chips[key].text,
        borderRadius: 999,
      }}
    />
  );
}

export default function AdminAssetDetail() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const documentsRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectError, setRejectError] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);

  const {
    data: res,
    isLoading,
    isError,
    refetch,
  } = useGetAdminAssetDetailQuery(assetId, { skip: !assetId });

  const [approveAsset, { isLoading: approving }] = useApproveAdminAssetMutation();
  const [rejectAssetMutation] = useRejectAdminAssetMutation();

  const {
    data: verificationDetailRes,
    isLoading: verificationDetailLoading,
    isFetching: verificationDetailFetching,
    isError: verificationDetailIsError,
    error: verificationDetailErr,
  } = useGetVerificationDetailQuery(
    { type: reviewTarget?.type ?? "", id: reviewTarget?.id ?? "" },
    { skip: !reviewTarget?.id || !reviewTarget?.type },
  );
  const verificationDetailPayload = verificationDetailRes?.data;
  const verificationDetailBusy = Boolean(reviewTarget) && (verificationDetailLoading || verificationDetailFetching);

  const d = res?.data;
  const uiStatus = normalizeUiStatus(d?.status);

  const mainImageUrl = d?.assetImages?.mainImage?.signedUrl || "";
  const categoryName =
    (typeof d?.assetCategory === "object" && d?.assetCategory?.name) || d?.assetCategory || "—";
  const isRealEstate = String(categoryName).toLowerCase().replace(/\s+/g, "").includes("realestate");

  const verificationButtons = [
    ...(isRealEstate && d?.regaVerificationId
      ? [{ type: "REGA", label: "REGA", id: d.regaVerificationId }]
      : []),
    ...(d?.valuationReportVerificationId
      ? [{ type: "TAUFEEQ", label: "Valuation Report", id: d.valuationReportVerificationId }]
      : []),
  ];
  const commodity = d?.basicInfo?.commodityAssetDetails;
  const ownerName = d?.createdBy
    ? [d.createdBy.firstName, d.createdBy.lastName].filter(Boolean).join(" ") || d.createdBy.email || "—"
    : "—";
  const locationDisplay =
    normalizeLocationString(commodity?.location) || normalizeLocationString(d?.location) || null;
  const showLocation = Boolean(locationDisplay);

  const fp = d?.fundingProgress || {};
  const pct = Math.min(100, Math.max(0, Number(fp.fundingProgressPercent) || 0));
  const fin = d?.financialDetails || {};
  const tokenPrice = fp.tokenPrice ?? d?.tokenEconomics?.tokenPriceUSD;
  const minInv = fp.minimumInvestment ?? fin.minimumInvestment;
  const totalRaised = fp.totalRaised ?? 0;

  const files = useMemo(() => (Array.isArray(d?.files) ? d.files : []), [d?.files]);

  const rejectAssetRow = useMemo(() => {
    if (!d) return null;
    const name =
      d.basicInfo?.assetName || d.assetName || d.name || d.title || "—";
    return {
      id: d._id ?? d.id,
      name,
      owner: ownerName,
      raw: d,
    };
  }, [d, ownerName]);

  const handleApprove = async () => {
    if (!d?._id) return;
    try {
      await approveAsset({ assetId: String(d._id) }).unwrap();
      refetch();
    } catch {
      /* toast from API */
    }
  };

  const handleRejectConfirm = async (asset, payload) => {
    const assetId = asset?.id ?? asset?.raw?._id ?? asset?.raw?.id;
    if (assetId == null || String(assetId).trim() === "") {
      setRejectError("Missing asset id.");
      throw new Error("Missing asset id");
    }
    const filesNeedToBeUpdated = Array.isArray(payload?.filesNeedToBeUpdated)
      ? payload.filesNeedToBeUpdated.map((x) => String(x))
      : [];
    setRejectError(null);
    try {
      await rejectAssetMutation({
        assetId: String(assetId).trim(),
        rejectionReason: String(payload?.reason ?? ""),
        filesNeedToBeUpdated,
      }).unwrap();
      refetch();
    } catch (e) {
      const msg = e?.data?.message || e?.error || "Could not reject asset.";
      setRejectError(msg);
      throw e;
    }
  };

  const scrollToDocuments = () => {
    documentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!assetId) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
          onClick={() => navigate("/admin/assets")}
          sx={{ textTransform: "none", mb: 2 }}
        >
          Back
        </Button>
        <Typography color="error">Invalid asset.</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !d) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
          onClick={() => navigate("/admin/assets")}
          sx={{ textTransform: "none", mb: 2, color: "text.primary" }}
        >
          Back to assets
        </Button>
        <Typography color="error">Could not load asset details.</Typography>
      </Box>
    );
  }

  const showReviewActions = uiStatus === STATUS.UNDER_REVIEW;

  return (
    <Box sx={{ pb: 4 }}>
      <Button
        onClick={() => navigate("/admin/assets")}
        startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
        sx={{
          textTransform: "none",
          color: "text.primary",
          mb: 2,
          px: 0,
          "&:hover": { backgroundColor: "transparent" },
        }}
      >
        Back to Asset Management
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "flex-start" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary", letterSpacing: -0.5 }}>
            Asset Details
          </Typography>
          <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
            Complete asset information and performance
          </Typography>
        </Box>
        <Stack spacing={1} alignItems={{ xs: "stretch", sm: "flex-end" }}>
          {showReviewActions ? (
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                disabled={approving}
                onClick={handleApprove}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                Approve Asset
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<HighlightOff />}
                onClick={() => {
                  setRejectError(null);
                  setRejectOpen(true);
                }}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                Reject Asset
              </Button>
            </Stack>
          ) : null}
          {verificationButtons.length > 0 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
              {verificationButtons.map(({ type, label, id }) => (
                <Button
                  key={type}
                  variant="outlined"
                  size="small"
                  onClick={() => setReviewTarget({ type, id, listUserId: String(d?.createdBy?._id || "") })}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                >
                  {label}
                </Button>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </Stack>

      {/* Summary card */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
          overflow: "hidden",
        }}
      >
        <Grid container>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ position: "relative", height: { xs: 240, md: "100%" }, minHeight: 280 }}>
              {mainImageUrl ? (
                <Box
                  component="img"
                  src={mainImageUrl}
                  alt={d?.basicInfo?.assetName || "Asset"}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", p: 1.2 }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: 280,
                    bgcolor: "background.subtle",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.muted">No image</Typography>
                </Box>
              )}
              <Box sx={{ position: "absolute", top: 16, left: 16 }}>
                <StatusBadge status={uiStatus} />
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="h5" fontWeight={800} sx={{ color: "text.primary", mb: 1.5 }}>
                {d?.basicInfo?.assetName || "—"}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                <Chip
                  label={categoryName}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    borderRadius: 999,
                    bgcolor: "background.subtle",
                    border: (theme) => `1px solid ${theme.palette.border.light}`,
                  }}
                />
                {commodity?.commodityType ? (
                  <Chip label={commodity.commodityType} size="small" color="primary" variant="outlined" />
                ) : null}
                {d?.regaVerificationStatus && (
                  <Chip 
                    label={`REGA: ${d.regaVerificationStatus}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {d?.valuationReportVerificationStatus && (
                  <Chip 
                    label={`Valuation Report: ${d.valuationReportVerificationStatus}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, lineHeight: 1.6 }}>
                {d?.basicInfo?.description || "—"}
              </Typography>
              <Stack spacing={1.25} mb={2.5}>
                {showLocation ? (
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <LocationOn sx={{ fontSize: 20, color: "text.muted", mt: 0.1 }} />
                    <Box>
                      <Typography variant="caption" color="text.muted" fontWeight={600}>
                        Location
                      </Typography>
                      <Typography fontWeight={600} fontSize={14}>
                        {locationDisplay}
                      </Typography>
                    </Box>
                  </Stack>
                ) : null}
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <PersonOutline sx={{ fontSize: 20, color: "text.muted", mt: 0.1 }} />
                  <Box>
                    <Typography variant="caption" color="text.muted" fontWeight={600}>
                      Owner
                    </Typography>
                    <Typography fontWeight={600} fontSize={14}>
                      {ownerName}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <CalendarTodayOutlined sx={{ fontSize: 20, color: "text.muted", mt: 0.1 }} />
                  <Box>
                    <Typography variant="caption" color="text.muted" fontWeight={600}>
                      Listed date
                    </Typography>
                    <Typography fontWeight={600} fontSize={14}>
                      {formatListedDate(d?.createdAt)}
                    </Typography>
                  </Box>
                </Stack>
                {d?.deployment?.mintTxHash ? (
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TagOutlined sx={{ fontSize: 20, color: "text.muted", mt: 0.1 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.muted" fontWeight={600}>
                        Transaction Hash
                      </Typography>
                      <Typography
                        component="a"
                        href={`https://etherscan.io/tx/${d.deployment.mintTxHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontWeight={600}
                        fontSize={14}
                        sx={{
                          display: "block",
                          wordBreak: "break-all",
                          fontFamily: "monospace",
                          color: "primary.main",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {d.deployment.mintTxHash}
                      </Typography>
                    </Box>
                  </Stack>
                ) : null}
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionOutlined />}
                  onClick={scrollToDocuments}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                >
                  View Documents
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<SarIcon sx={{ color: "primary.main" }} />}
            title="Asset Value"
            value={formatSar(fin.totalAssetValue)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<TrendingUp sx={{ color: "success.main" }} />}
            title="Expected ROI"
            value={
              fin.expectedROI != null && Number.isFinite(Number(fin.expectedROI))
                ? `${Number(fin.expectedROI)}%`
                : "—"
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<Groups sx={{ color: "secondary.main" }} />}
            title="Investors"
            value={String(fp.investorCount ?? 0)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<ShowChart sx={{ color: "info.main" }} />}
            title="Tokens Sold"
            value={`${pct.toFixed(pct % 1 === 0 ? 0 : 1)}`}
          />
        </Grid>
      </Grid>

      {/* Funding progress */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Typography fontWeight={700} fontSize={18}>
              Funding Progress
            </Typography>
            <Typography variant="body2" color="text.muted" sx={{ mt: 0.5 }}>
              {(fp.tokensSold ?? 0).toLocaleString()} of {(fp.totalSupply ?? 0).toLocaleString()} tokens sold
            </Typography>
          </Box>
          <Typography fontWeight={700} fontSize={18} color="primary.main">
            {pct.toFixed(1)}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 10,
            borderRadius: 999,
            mb: 2.5,
            bgcolor: "action.hover",
            "& .MuiLinearProgress-bar": { borderRadius: 999 },
          }}
        />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.muted" fontWeight={600}>
              Token Price
            </Typography>
            <Typography fontWeight={700}>{tokenPrice != null ? formatSar(tokenPrice) : "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.muted" fontWeight={600}>
              Min. Investment
            </Typography>
            <Typography fontWeight={700}>{minInv != null ? formatSar(minInv) : "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.muted" fontWeight={600}>
              Total Raised
            </Typography>
            <Typography fontWeight={700}>{formatSar(totalRaised)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            pt: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.border.subtle}`,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          }}
        >
          <Tab label="Documents" id="asset-tab-0" />
        </Tabs>
        <TabPanel value={tab} index={0}>
          <Box ref={documentsRef} sx={{ px: 2, pb: 2 }}>
            <Typography fontWeight={700} fontSize={18}>
              Asset Documents
            </Typography>
            <Typography variant="body2" color="text.muted" sx={{ mb: 2 }}>
              Legal and compliance documents
            </Typography>
            {files.length === 0 ? (
              <Typography color="text.muted">No documents uploaded.</Typography>
            ) : (
              <Stack spacing={0}>
                {files.map((file, idx) => {
                  const tag = categoryTagColor(file.type);
                  const title = docDisplayTitle(file.type);
                  const uploaded = d?.createdAt
                    ? new Date(d.createdAt).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "—";
                  const url = file.signedUrl || file.url;
                  return (
                    <Stack
                      key={file._id || file.key || `${file.type}-${idx}`}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        py: 2,
                        borderTop: idx === 0 ? "none" : (theme) => `1px solid ${theme.palette.border.subtle}`,
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        <InsertDriveFile sx={{ color: "primary.main", flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography fontWeight={600} fontSize={15}>
                              {title}
                            </Typography>
                            {/* <Chip
                              label={adminDocTypeLabel(file.type)}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                bgcolor: tag.bg,
                                color: tag.color,
                              }}
                            /> */}
                          </Stack>
                          <Typography variant="caption" color="text.muted">
                            Uploaded {uploaded}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
                        {url ? (
                          <IconButton
                            size="small"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            component="a"
                            aria-label={`Open ${title}`}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        ) : null}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Box>
        </TabPanel>
      </Paper>

      <RejectAssetModal
        open={rejectOpen}
        onClose={() => {
          setRejectOpen(false);
          setRejectError(null);
        }}
        asset={rejectAssetRow}
        onConfirm={handleRejectConfirm}
        errorMessage={rejectError}
      />

      <VerificationReviewDialog
        open={Boolean(reviewTarget)}
        onClose={() => setReviewTarget(null)}
        loading={verificationDetailBusy}
        fetchError={verificationDetailIsError ? verificationDetailErr : null}
        detail={verificationDetailIsError ? null : verificationDetailPayload}
        detailQueryId={reviewTarget?.id}
        listUserId={reviewTarget?.listUserId}
        assetOwnersTab={true}
        verificationType={reviewTarget?.type}
        onActionSuccess={() => refetch()}
      />
    </Box>
  );
}
